<template>
  <div class="supplier-coupon-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('supplierCoupon.couponInfo')" :subtitle="$t('supplierCoupon.page.subtitle')" />

    <el-row :gutter="16">
      <!-- 左侧：供应商列表 -->
      <el-col :xs="24" :md="6" :lg="5">
        <div class="ds-panel ds-panel--padded">
          <div class="ds-panel__header">
            <span class="ds-panel__title">{{ $t('supplierCoupon.supplier') }}</span>
          </div>
          <ul class="ds-tree">
            <li v-for="(supplier, index) in suppliers" :key="index" class="ds-tree__item"
              :class="{ 'ds-tree__item--active': supplier.supplierId === curSupplier.supplierId }"
              @click="switchSupplier(supplier)">
              {{ supplier.supplierName }}
            </li>
          </ul>
          <el-empty v-if="!suppliers.length" :description="$t('supplierCoupon.page.noSupplier')" :image-size="60" />
        </div>
      </el-col>

      <!-- 右侧：查询 + 优惠券列表 -->
      <el-col :xs="24" :md="18" :lg="19">
        <!-- (2) SearchPanel -->
        <ds-search-panel>
          <el-row :gutter="16">
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="conditions.name" clearable
                :placeholder="$t('supplierCoupon.name')" @keyup.enter.native="querySupplierCoupon" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="conditions.businessKey" clearable
                :placeholder="$t('supplierCoupon.businessId')" @keyup.enter.native="querySupplierCoupon" />
            </el-col>
          </el-row>

          <template #actions>
            <el-button type="primary" icon="el-icon-search" @click="querySupplierCoupon">
              {{ $t('supplierCoupon.search') }}
            </el-button>
            <el-button icon="el-icon-refresh-left" @click="resetSupplierCoupon">
              {{ $t('common.reset') }}
            </el-button>
          </template>
        </ds-search-panel>

        <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
        <div class="ds-panel">
          <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false" :show-refresh="true"
            @update:density="density = $event" @refresh="getSupplierCouponList">
            <template #left>
              <span class="ds-panel__title">{{ $t('supplierCoupon.couponInfo') }}</span>
            </template>
            <template #right>
              <el-button type="primary" size="mini" icon="el-icon-plus" @click="openAddDialog">
                {{ $t('supplierCoupon.add') }}
              </el-button>
            </template>
          </ds-toolbar>

          <ds-data-table :data="supplierCoupons" :loading="loading" :error="hasError" :error-message="errorMessage"
            :density="density" border :empty-description="$t('supplierCoupon.page.empty')" @retry="getSupplierCouponList">
            <el-table-column prop="couponId" :label="$t('supplierCoupon.name')" align="center" width="140">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.couponId || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="name" :label="$t('supplierCoupon.name')" align="left" min-width="160" show-overflow-tooltip />
            <el-table-column prop="supplierName" :label="$t('supplierCoupon.supplier')" align="center" min-width="140" show-overflow-tooltip />
            <el-table-column prop="businessKey" :label="$t('supplierCoupon.thirdPartyId')" align="center" width="160">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.businessKey || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="valuePrice" :label="$t('supplierCoupon.price')" align="right" width="110">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.valuePrice }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="remark" :label="$t('supplierCoupon.remark')" align="center" min-width="140" show-overflow-tooltip>
              <template slot-scope="scope">
                {{ scope.row.remark || '-' }}
              </template>
            </el-table-column>
            <el-table-column :label="$t('supplierCoupon.operation')" align="center" width="140" fixed="right">
              <template slot-scope="scope">
                <el-button size="mini" type="text" @click="openEditDialog(scope.row)">
                  {{ $t('supplierCoupon.edit') }}
                </el-button>
                <el-button size="mini" type="text" class="ds-btn-danger" @click="openDeleteDialog(scope.row)">
                  {{ $t('supplierCoupon.delete') }}
                </el-button>
              </template>
            </el-table-column>
          </ds-data-table>

          <ds-pagination-bar :total="pagination.total" :page="pagination.current" :size="pagination.size"
            :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange" />
        </div>
      </el-col>
    </el-row>

    <add-supplier-coupon ref="addDialog" @success="handleAddSuccess" />
    <edit-supplier-coupon ref="editDialog" @success="handleEditSuccess" />
    <delete-supplier-coupon ref="deleteDialog" @success="handleDeleteSuccess" />
  </div>
</template>

<script>
import { getSupplierList, getSupplierCouponList } from '@/api/scm/supplierCouponApi'
import AddSupplierCoupon from '@/components/scm/addSupplierCoupon'
import EditSupplierCoupon from '@/components/scm/editSupplierCoupon'
import DeleteSupplierCoupon from '@/components/scm/deleteSupplierCoupon'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'SupplierCouponList',
  components: {
    AddSupplierCoupon,
    EditSupplierCoupon,
    DeleteSupplierCoupon,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar
  },
  data() {
    return {
      suppliers: [],
      curSupplier: {},
      supplierCoupons: [],
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      conditions: {
        name: '',
        businessKey: '',
        supplierId: ''
      },
      pagination: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  mounted() {
    this.getSupplierList()
  },
  methods: {
    async getSupplierList() {
      try {
        const res = await getSupplierList({
          page: 1,
          row: 100
        })
        this.suppliers = Array.isArray(res.data) ? res.data : []
        if (this.suppliers.length > 0) {
          this.switchSupplier(this.suppliers[0])
        }
      } catch (error) {
        console.error('获取供应商列表失败:', error)
        this.$message.error(this.$t('supplierCoupon.fetchError'))
      }
    },

    async getSupplierCouponList() {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = {
          ...this.conditions,
          page: this.pagination.current,
          row: this.pagination.size
        }
        const res = await getSupplierCouponList(params)
        this.supplierCoupons = Array.isArray(res.data) ? res.data : []
        this.pagination.total = res.total || 0
      } catch (error) {
        console.error('获取供应商优惠券列表失败:', error)
        this.hasError = true
        this.errorMessage = error.message || this.$t('supplierCoupon.page.loadFailed')
        this.$message.error(this.$t('supplierCoupon.fetchError'))
      } finally {
        this.loading = false
      }
    },

    switchSupplier(supplier) {
      this.curSupplier = supplier
      this.conditions.supplierId = supplier.supplierId
      this.pagination.current = 1
      this.getSupplierCouponList()
    },

    querySupplierCoupon() {
      this.pagination.current = 1
      this.getSupplierCouponList()
    },

    resetSupplierCoupon() {
      this.conditions.name = ''
      this.conditions.businessKey = ''
      this.pagination.current = 1
      this.getSupplierCouponList()
    },

    handleSizeChange(size) {
      this.pagination.size = size
      this.pagination.current = 1
      this.getSupplierCouponList()
    },

    handleCurrentChange(current) {
      this.pagination.current = current
      this.getSupplierCouponList()
    },

    openAddDialog() {
      if (!this.curSupplier.supplierId) {
        this.$message.warning(this.$t('supplierCoupon.supplierRequired'))
        return
      }
      this.$refs.addDialog.open({
        supplierId: this.curSupplier.supplierId
      })
    },

    openEditDialog(row) {
      this.$refs.editDialog.open(row)
    },

    openDeleteDialog(row) {
      this.$refs.deleteDialog.open(row)
    },

    handleAddSuccess() {
      this.getSupplierCouponList()
    },

    handleEditSuccess() {
      this.getSupplierCouponList()
    },

    handleDeleteSuccess() {
      this.getSupplierCouponList()
    }
  }
}
</script>

<style lang="scss" scoped>
.supplier-coupon-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;
  margin-bottom: var(--ds-spacing-lg);

  &--padded {
    padding: var(--ds-spacing-lg);
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: var(--ds-spacing-md);
    margin-bottom: var(--ds-spacing-md);
    border-bottom: 1px solid var(--ds-gray-200);
  }

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

.ds-tree {
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: calc(100vh - 280px);
  overflow-y: auto;

  &__item {
    padding: 9px 12px;
    border-bottom: 1px solid var(--ds-gray-200);
    cursor: pointer;
    font-size: var(--ds-font-size-md);
    color: var(--ds-gray-700);
    transition: background-color 0.2s, color 0.2s;

    &:hover {
      background-color: var(--ds-gray-100);
    }

    &--active {
      background-color: var(--ds-primary);
      color: #fff;
    }
  }
}

.ds-btn-danger {
  color: var(--ds-danger) !important;
}
</style>
